import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Round } from '../data/types';
import PlayView from '../play/PlayView';

/* Playing a premade series he picked: /real-or-fake/modes/series/:roundId
 *
 * Reuses the roster-gated preview payload the team already uses to check their own work — the
 * same fields a public share link serves, minus internal notes and attribution. No new endpoint
 * and no second serialiser, so what Adorian plays cannot drift from what Alyssa previewed.
 *
 * Fetched once and frozen, as everywhere on the play side: a series that changed itself
 * mid-take would be worse than a stale one.
 */

export default function ModesPlaySeries() {
  const { roundId = '' } = useParams();
  const [round, setRound] = useState<Round | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.preview(roundId);
        if (!cancelled) setRound(loaded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load that series.');
      }
    })();
    return () => { cancelled = true; };
  }, [roundId]);

  /* Every failure state here sends him back to the picker rather than to the builder — he has
     no reason to end up in an editor. */
  if (error) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Could not open that series</div>
        <div className="sub">{error}</div>
        <Link className="btn primary" to="/modes/series" style={{ textDecoration: 'none' }}>Pick Another</Link>
      </div>
    );
  }

  if (!round) return <div className="centred"><div className="big">Loading series…</div></div>;

  if (round.items.length === 0) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">This series is empty</div>
        <div className="sub">No headlines have been added to it yet.</div>
        <Link className="btn primary" to="/modes/series" style={{ textDecoration: 'none' }}>Pick Another</Link>
      </div>
    );
  }

  return <PlayView round={round} session={{ source: 'modes', key: roundId }} />;
}
